import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface Book {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  modules: number;
  color: string;
  accentColor: string;
  fileName: string;
  topics: string[];
}

const books: Book[] = [
  {
    id: 'adhd-cbt',
    title: 'Mastering Your Mind',
    subtitle: 'A Comprehensive CBT Program for Adults with ADHD',
    description: 'Building the Bridge Between Intention and Action. This evidence-based program helps you understand your ADHD brain and develop practical strategies for attention, organization, and emotional regulation.',
    duration: '6-8 Weeks',
    modules: 7,
    color: '#2B4570',
    accentColor: '#3D5A80',
    fileName: 'Mastering-Your-Mind-CBT-ADHD-Program.docx',
    topics: ['Understanding ADHD', 'Building Routine', 'Overcoming Procrastination', 'Enhancing Focus', 'Organization', 'Emotional Management', 'Maintaining Momentum']
  },
  {
    id: 'autism-cbt',
    title: 'The Autistic Navigator',
    subtitle: 'A Comprehensive CBT Program for the Autistic Mind',
    description: 'Understanding, Accepting, and Thriving. A neurodiversity-affirming approach to building skills, managing challenges, and honoring your authentic self.',
    duration: '8-12 Weeks',
    modules: 7,
    color: '#1A5276',
    accentColor: '#2471A3',
    fileName: 'The-Autistic-Navigator-CBT-Program.docx',
    topics: ['Understanding Your Brain', 'Sensory Regulation', 'Emotional Awareness', 'Social Navigation', 'Communication', 'Flexibility & Change', 'Building Your Life']
  },
  {
    id: 'social-anxiety-cbt',
    title: 'Beyond The Fear',
    subtitle: 'A Comprehensive CBT Program for Social Anxiety',
    description: 'Breaking free from the grip of social anxiety. Learn evidence-based techniques to challenge negative thoughts, build confidence, and engage more fully in social situations.',
    duration: '8-10 Weeks',
    modules: 7,
    color: '#6B4C9A',
    accentColor: '#8B5CF6',
    fileName: 'Beyond-The-Fear-Social-Anxiety-Program.docx',
    topics: ['Understanding Social Anxiety', 'Cognitive Restructuring', 'Exposure Techniques', 'Building Confidence', 'Social Skills', 'Managing Setbacks', 'Long-term Recovery']
  },
  {
    id: 'trauma-cbt',
    title: 'Healing From Trauma',
    subtitle: 'A Comprehensive CBT Program for Trauma Recovery',
    description: 'A compassionate, evidence-based approach to processing trauma and reclaiming your life. Develop coping skills, process difficult experiences, and build resilience.',
    duration: '10-14 Weeks',
    modules: 8,
    color: '#4A7C59',
    accentColor: '#68A67D',
    fileName: 'Healing-From-Trauma-CBT-Program.docx',
    topics: ['Understanding Trauma', 'Safety & Stabilization', 'Coping Skills', 'Processing Experiences', 'Cognitive Processing', 'Building Resilience', 'Relationships', 'Moving Forward']
  }
];

const colors = {
  deep: '#0D0A0F',
  accent1: '#FCCB06',
  accent2: '#E5A83B',
  highlight: '#FFEBA3',
  text: '#FFFCF0',
};

export default function Library() {
  const [loaded, setLoaded] = useState(false);
  const [hoveredBook, setHoveredBook] = useState<string | null>(null);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: colors.deep,
        opacity: loaded ? 1 : 0,
        transition: 'opacity 1s ease-in-out'
      }}
    >
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {/* Bookshelves pattern */}
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `repeating-linear-gradient(
              0deg,
              transparent,
              transparent 100px,
              ${colors.accent1}20 100px,
              ${colors.accent1}20 102px
            )`
          }}
        />
        {/* Warm glow */}
        <div
          className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[80vw] h-[60vh] rounded-full opacity-15"
          style={{
            background: `radial-gradient(ellipse, ${colors.accent2}40, transparent 70%)`,
            filter: 'blur(100px)'
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-8 py-12">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="text-white/60 hover:text-amber-200 transition-colors text-sm tracking-widest"
          >
            &larr; BACK TO HOME
          </Link>
          <h1 className="text-2xl font-thin tracking-wider">
            THE <span style={{ color: colors.highlight }}>LIBRARY</span>
          </h1>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-10 px-8 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <div className="text-6xl mb-8">📚</div>
          <h2 className="text-5xl md:text-6xl font-thin mb-6">
            Therapeutic <span style={{ color: colors.highlight }}>Reading</span>
          </h2>
          <p className="text-xl text-white/60 font-extralight max-w-2xl mx-auto leading-relaxed">
            Evidence-based Cognitive Behavioral Therapy programs designed to help you understand your mind and build practical skills for everyday life.
          </p>
        </div>
      </section>

      {/* Books Grid */}
      <section className="relative z-10 px-8 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {books.map((book) => (
              <Link
                key={book.id}
                to={`/library/${book.id}`}
                className="group relative"
                onMouseEnter={() => setHoveredBook(book.id)}
                onMouseLeave={() => setHoveredBook(null)}
              >
                {/* Book Card */}
                <div
                  className="relative rounded-3xl overflow-hidden transition-all duration-700 transform group-hover:-translate-y-4"
                  style={{
                    background: `linear-gradient(135deg, ${book.color}40, ${colors.deep})`,
                    border: `1px solid ${hoveredBook === book.id ? book.accentColor : 'rgba(255,255,255,0.1)'}`,
                    boxShadow: hoveredBook === book.id
                      ? `0 30px 60px -20px ${book.color}50, 0 0 40px ${book.accentColor}20`
                      : '0 20px 40px -20px rgba(0,0,0,0.5)'
                  }}
                >
                  {/* Book spine decoration */}
                  <div
                    className="absolute left-0 top-0 bottom-0 w-3"
                    style={{ background: `linear-gradient(to right, ${book.color}, transparent)` }}
                  />

                  <div className="p-10 pl-12">
                    {/* Header */}
                    <div className="mb-8">
                      <h3
                        className="text-3xl md:text-4xl font-thin mb-3 transition-colors duration-500"
                        style={{ color: hoveredBook === book.id ? book.accentColor : colors.text }}
                      >
                        {book.title}
                      </h3>
                      <p className="text-lg text-white/70 font-extralight">
                        {book.subtitle}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-white/50 leading-relaxed mb-8 font-extralight">
                      {book.description}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 mb-8">
                      <span
                        className="px-4 py-2 rounded-full text-sm"
                        style={{ background: `${book.color}40`, color: book.accentColor }}
                      >
                        {book.modules} Modules
                      </span>
                      <span
                        className="px-4 py-2 rounded-full text-sm"
                        style={{ background: `${book.color}40`, color: book.accentColor }}
                      >
                        {book.duration}
                      </span>
                      <span
                        className="px-4 py-2 rounded-full text-sm"
                        style={{ background: `${book.color}40`, color: book.accentColor }}
                      >
                        Self-Paced
                      </span>
                    </div>

                    {/* Topics Preview */}
                    <div className="mb-8">
                      <h4 className="text-sm text-white/40 tracking-wider mb-3">TOPICS COVERED</h4>
                      <div className="flex flex-wrap gap-2">
                        {book.topics.slice(0, 4).map((topic, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 rounded-full text-xs"
                            style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.5)' }}
                          >
                            {topic}
                          </span>
                        ))}
                        {book.topics.length > 4 && (
                          <span className="px-3 py-1 text-xs text-white/30">
                            +{book.topics.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* CTA */}
                    <div
                      className="flex items-center gap-3 transition-all duration-500"
                      style={{ color: book.accentColor }}
                    >
                      <span className="text-sm tracking-wider font-medium">START READING</span>
                      <span className="transform group-hover:translate-x-2 transition-transform duration-500">&rarr;</span>
                    </div>
                  </div>

                  {/* Decorative corner */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 opacity-20"
                    style={{
                      background: `radial-gradient(circle at top right, ${book.accentColor}, transparent 70%)`
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Author Note */}
      <section className="relative z-10 px-8 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div
            className="p-12 rounded-3xl"
            style={{
              background: 'rgba(13, 10, 15, 0.8)',
              border: `1px solid ${colors.accent1}20`
            }}
          >
            <div className="text-4xl mb-6">✨</div>
            <h3 className="text-2xl font-thin mb-6" style={{ color: colors.highlight }}>
              About These Programs
            </h3>
            <p className="text-white/60 leading-relaxed font-extralight mb-6">
              These CBT programs are designed with neurodiversity-affirming principles. They focus on understanding and working with your unique brain, not against it. Each module includes practical exercises, worksheets, and evidence-based strategies.
            </p>
            <p className="text-white/40 text-sm font-extralight">
              Created by Birger Moëll, PhD &mdash; Licensed Psychologist specializing in CBT
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-16 text-center">
        <Link
          to="/"
          className="inline-block px-8 py-3 rounded-full text-sm tracking-wider transition-all hover:scale-105"
          style={{
            background: `linear-gradient(to right, ${colors.accent1}40, ${colors.accent2}30)`,
            border: `1px solid ${colors.accent1}50`
          }}
        >
          BACK TO MAIN SITE
        </Link>
      </footer>
    </div>
  );
}
